import { computed, ComputedRef, Ref, ref, watch } from 'vue';
import { TaskConfigStep } from '@/types/TaskConfig';
import { getObservedSessionId } from '@/composables/v2/metrics/useMetrics';
import useAuthenticatedStudent from '@/composables/v2/metrics/useAuthenticatedStudent';

/**
 * The audio a step may show on the right of its header, in place of the image (eprocom-pwa#852,
 * backend spec `specs/V3/media-components/header-audio.md` §4): its play limit, the « listen first »
 * rule on « Suivant », and the plays the student spent.
 *
 * The plays and the listened state are kept in `localStorage`, per session × student × task × step:
 * a reload gives nothing back. They are cleared at TASK_END, and at the end and start of a session.
 * Without a session — the test preview — they live in memory only: nothing would ever clear them.
 */

const STORAGE_PREFIX = 'header_audio_progress:';
/** Stands in for an id the app does not know: a student, or the session when clearing outside one. */
const UNKNOWN_ID = 'none';

type HeaderAudioProgress = {
    plays: number;
    listened: boolean;
};

export interface HeaderAudio {
    /** `header_audio` as in the config, or null when the step has none or shows its image instead. */
    audioPath: ComputedRef<string | null>;
    /** null → unlimited plays, no counter. */
    maxPlays: ComputedRef<number | null>;
    remainingPlays: ComputedRef<number | null>;
    /** Every play spent, or the audio cannot be played. */
    isPlayDisabled: ComputedRef<boolean>;
    isListenRequirementMet: ComputedRef<boolean>;
    /** The audio really started: one play is spent. */
    registerPlay: () => void;
    /** A play reached the end of the audio. */
    registerListened: () => void;
    /** The audio cannot be loaded or started: it no longer holds the student. */
    registerUnavailable: () => void;
}

const { studentId } = useAuthenticatedStudent();

function scopePrefix(): string {
    return `${STORAGE_PREFIX}${getObservedSessionId() ?? UNKNOWN_ID}:${studentId.value ?? UNKNOWN_ID}:`;
}

function taskPrefix(taskId: number | string): string {
    return `${scopePrefix()}${taskId}:`;
}

function readProgress(key: string): HeaderAudioProgress {
    try {
        const stored = JSON.parse(localStorage.getItem(key) ?? 'null');

        if (stored && Number.isInteger(stored.plays) && stored.plays >= 0 && typeof stored.listened === 'boolean') {
            return { plays: stored.plays, listened: stored.listened };
        }
    } catch {
        // A corrupted entry reads as nothing spent.
    }

    return { plays: 0, listened: false };
}

function writeProgress(key: string, progress: HeaderAudioProgress): void {
    try {
        localStorage.setItem(key, JSON.stringify(progress));
    } catch {
        // Storage full or refused (private browsing): the count still holds until the page reloads.
    }
}

function removeProgressEntries(shouldRemove: (key: string) => boolean): void {
    Object.keys(localStorage)
        .filter((key) => key.startsWith(STORAGE_PREFIX) && shouldRemove(key))
        .forEach((key) => localStorage.removeItem(key));
}

/** TASK_END: a later run of the task starts with every play again. */
export function clearHeaderAudioProgressOfTask(taskId: number | string): void {
    const prefix = taskPrefix(taskId);

    removeProgressEntries((key) => key.startsWith(prefix));
}

/** Session start: a reload makes the student join the same session again, whose plays must survive. */
export function clearHeaderAudioProgressOfOtherSessions(sessionId: string): void {
    const prefix = `${STORAGE_PREFIX}${sessionId}:`;

    removeProgressEntries((key) => !key.startsWith(prefix));
}

/** Session end. */
export function clearAllHeaderAudioProgress(): void {
    removeProgressEntries(() => true);
}

/** A hand-edited config may hold 0, a negative or a decimal number: it reads as no limit. */
function toMaxPlays(value: unknown): number | null {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : null;
}

export default function useHeaderAudio(taskId: number, activeStep: Ref<TaskConfigStep>): HeaderAudio {
    const progress = ref<HeaderAudioProgress>({ plays: 0, listened: false });
    const isUnavailable = ref(false);
    const storageKey = (): string => `${taskPrefix(taskId)}${activeStep.value.id}`;
    const isPersisted = (): boolean => getObservedSessionId() !== undefined;

    watch(
        () => activeStep.value.id,
        () => {
            progress.value = isPersisted() ? readProgress(storageKey()) : { plays: 0, listened: false };
            isUnavailable.value = false;
        },
        { immediate: true }
    );

    // The image wins when a hand-edited config carries both.
    const audioPath = computed(() => (activeStep.value.instruction_img ? null : activeStep.value.header_audio || null));
    const maxPlays = computed(() => (audioPath.value ? toMaxPlays(activeStep.value.header_audio_max_plays) : null));
    const remainingPlays = computed(() =>
        maxPlays.value === null ? null : Math.max(maxPlays.value - progress.value.plays, 0)
    );
    const isExhausted = computed(() => remainingPlays.value === 0);
    const mustListen = computed(() => audioPath.value !== null && activeStep.value.header_audio_must_listen === true);

    const isPlayDisabled = computed(() => isExhausted.value || isUnavailable.value);
    // Spent plays or a broken file never hold the student (FR-MED-507, Q-MED-09).
    const isListenRequirementMet = computed(
        () => !mustListen.value || progress.value.listened || isExhausted.value || isUnavailable.value
    );

    const saveProgress = (next: HeaderAudioProgress): void => {
        progress.value = next;
        if (isPersisted()) {
            writeProgress(storageKey(), next);
        }
    };

    const registerPlay = (): void => {
        if (!isExhausted.value) {
            saveProgress({ ...progress.value, plays: progress.value.plays + 1 });
        }
    };

    const registerListened = (): void => {
        saveProgress({ ...progress.value, listened: true });
    };

    const registerUnavailable = (): void => {
        isUnavailable.value = true;
    };

    return {
        audioPath,
        maxPlays,
        remainingPlays,
        isPlayDisabled,
        isListenRequirementMet,
        registerPlay,
        registerListened,
        registerUnavailable,
    };
}
